import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/auth.store';
import { EmailTemplateBuilderEditor } from '@/components/documents/emails/EmailTemplateBuilderEditor';
import { useCreateEmailTemplate } from '@/hooks/emailTemplates';
import { EMAIL_TEMPLATE_KINDS, type EmailTemplateKind } from '@/types/emailTemplate';

type NewEmailSearch = { kind: EmailTemplateKind };

function isEmailTemplateKind(value: unknown): value is EmailTemplateKind {
  return EMAIL_TEMPLATE_KINDS.includes(value as EmailTemplateKind);
}

export const Route = createFileRoute('/_dashboard/dashboard/settings/email-templates/new')({
  // Validated against the kind list rather than a two-way ternary, which sent
  // every kind it did not name to ESTIMATE_SEND — so "New email" from the job
  // confirmation tab would have created an estimate email.
  validateSearch: (search): NewEmailSearch => ({
    kind: isEmailTemplateKind(search.kind) ? search.kind : 'ESTIMATE_SEND',
  }),
  beforeLoad: () => {
    const { permissions } = useAuthStore.getState();
    if (permissions.length > 0 && !permissions.includes('estimates:create')) {
      throw redirect({ to: '/dashboard/home' });
    }
  },
  component: NewEmailTemplatePage,
});

function NewEmailTemplatePage() {
  const navigate = useNavigate();
  const { kind } = Route.useSearch();
  const create = useCreateEmailTemplate();

  return (
    <EmailTemplateBuilderEditor
      // Remount on kind change: name / subject / layout reseed from that kind.
      key={kind}
      mode="create"
      kind={kind}
      onKindChange={(next) =>
        navigate({
          to: '/dashboard/settings/email-templates/new',
          search: { kind: next },
          replace: true,
        })
      }
      initialName=""
      initialSubject=""
      initialLayout={null}
      saving={create.isPending}
      onBack={() =>
        navigate({
          to: '/dashboard/settings/document-templates',
          search: { tab: 'emails', kind: kind },
        })
      }
      onSave={(name, subject, bodyHtml, layout, branding) =>
        create
          .mutateAsync({
            kind,
            name,
            ...(subject ? { subject } : {}),
            bodyHtml,
            layout,
            ...(branding ? { branding } : {}),
          })
          .then((row) =>
            navigate({
              to: '/dashboard/settings/email-templates/$id',
              params: { id: row.id },
            }),
          )
      }
    />
  );
}
